/**
 * Store Zustand pour la gestion des sessions
 * REUTILISABILITE : 100% - Compatible Next.js et React
 *
 * Ce store delegue toute la logique metier aux Use Cases
 * et ne gere que l'etat React (state management).
 */

import { create } from 'zustand';
import { container } from '../../infrastructure/di';
import type { Session } from '../../application/dto/dtos';

/**
 * Interface du store de sessions
 */
export interface SessionState {
  // Etat
  sessions: Session[];
  currentSessionId: string | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchSessions: (idUtilisateur: string, actives?: boolean) => Promise<void>;
  revokeSession: (idUtilisateur: string, idSession: string) => Promise<void>;
  revokeAllSessions: (idUtilisateur: string, motDePasse: string, conserverActuelle?: boolean) => Promise<void>;

  // Utilitaires
  setError: (error: string | null) => void;
  clearError: () => void;
  reset: () => void;
}

const initialState = {
  sessions: [],
  currentSessionId: null,
  isLoading: false,
  error: null,
};

export const useSessionStore = create<SessionState>((set, get) => ({
  ...initialState,

  fetchSessions: async (idUtilisateur: string, actives: boolean = true) => {
    set({ isLoading: true, error: null });
    try {
      const result = await container.listSessionsUseCase.execute({
        idUtilisateur,
        actives,
        limite: 100,
      });

      set({
        sessions: result.sessions,
        currentSessionId: result.sessionActuelle || null,
        isLoading: false,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur de recuperation des sessions';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  revokeSession: async (idUtilisateur: string, idSession: string) => {
    set({ isLoading: true, error: null });
    try {
      await container.revokeSessionUseCase.execute({ idUtilisateur, idSession });

      set((state) => ({
        sessions: state.sessions.filter((s) => s.id !== idSession),
        isLoading: false,
      }));

      await get().fetchSessions(idUtilisateur);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur de revocation de session';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  revokeAllSessions: async (
    idUtilisateur: string,
    motDePasse: string,
    conserverActuelle: boolean = true
  ) => {
    set({ isLoading: true, error: null });
    try {
      await container.revokeAllSessionsUseCase.execute({
        idUtilisateur,
        motDePasse,
        conserverActuelle,
      });

      if (conserverActuelle) {
        const currentSession = get().sessions.find((s) => s.id === get().currentSessionId);
        set({
          sessions: currentSession ? [currentSession] : [],
          isLoading: false,
        });
      } else {
        set({ sessions: [], currentSessionId: null, isLoading: false });
      }

      await get().fetchSessions(idUtilisateur);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur de revocation des sessions';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  setError: (error: string | null) => set({ error }),
  clearError: () => set({ error: null }),
  reset: () => set({ ...initialState }),
}));
